import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { driverKeys, notificationsApi } from '../../features/driver/driverApi';
import { ROUTES, routeTo } from '../../routes/paths';
import { Button } from '../ui/Button';
import type { Notification, NotificationType } from '../../types/api';

/** Cada cuanto se vuelve a preguntar por los avisos, en milisegundos. */
const REFRESH_INTERVAL = 60_000;

/** Cuantos caben en el panel sin convertirlo en un listado. */
const PANEL_SIZE = 8;

const TYPE_LABELS: Record<NotificationType, string> = {
  INCIDENT_REPORTED: 'Incidente',
  ORDER_PARTIALLY_COMPLETED: 'Orden con faltantes',
  EMAIL_FAILED: 'Correo fallido',
  LOW_STOCK: 'Stock bajo',
};

function formatWhen(value: string): string {
  return new Date(value).toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * A donde lleva un aviso.
 *
 * Un aviso que no se puede abrir obliga a buscar a mano lo que acaba de pasar.
 * Los tipos que todavia no tienen pantalla propia no navegan a ninguna parte,
 * en lugar de llevar a un sitio que no responde la pregunta.
 */
function destinationFor(notification: Notification): string | null {
  switch (notification.type) {
    case 'INCIDENT_REPORTED':
      return ROUTES.warehouse.incidents;
    case 'EMAIL_FAILED':
      return ROUTES.warehouse.emails;
    case 'ORDER_PARTIALLY_COMPLETED':
      return notification.entity_id === null
        ? ROUTES.warehouse.orders
        : routeTo(ROUTES.warehouse.orderDetail, { id: notification.entity_id });
    case 'LOW_STOCK':
      return ROUTES.warehouse.stock;
    default:
      return null;
  }
}

/**
 * Campana de la navbar con el contador real.
 *
 * Se refresca por sondeo cada minuto y no por conexion persistente: un aviso
 * que llega un minuto tarde no cambia ninguna decision, y una conexion abierta
 * por sesion cuesta bastante mas que una consulta contada.
 */
export function NotificationsMenu(): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: [...driverKeys.notifications, 'panel'],
    queryFn: () => notificationsApi.list({ page: 1, limit: PANEL_SIZE }),
    refetchInterval: REFRESH_INTERVAL,
  });

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => notificationsApi.markRead(ids),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: driverKeys.notifications });
    },
  });

  const items = query.data?.data ?? [];
  const unread = items.filter((item) => !item.is_read).length;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent): void => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  function open(notification: Notification): void {
    setIsOpen(false);
    if (!notification.is_read) {
      markRead.mutate([notification.id]);
    }

    const destination = destinationFor(notification);
    if (destination !== null) {
      void navigate(destination);
    }
  }

  return (
    <div ref={panelRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => {
          setIsOpen((value) => !value);
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={
          unread === 0
            ? 'Notificaciones: ninguna sin leer'
            : `Notificaciones: ${String(unread)} sin leer`
        }
        className="relative grid h-11 w-11 place-items-center rounded-card text-content hover:bg-primary-soft"
      >
        <FontAwesomeIcon icon={faBell} aria-hidden="true" />
        {unread > 0 ? (
          // El numero va escrito, no solo el punto de color: quien no
          // distingue los tonos se quedaria sin saber cuantos son.
          <span className="absolute right-1 top-1 min-w-4 rounded-full bg-danger px-1 text-[10px] font-semibold leading-4 text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-card border border-line bg-surface shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-sm font-semibold text-content">Notificaciones</span>
            {unread > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                isLoading={markRead.isPending}
                onClick={() => {
                  markRead.mutate(undefined);
                }}
              >
                Marcar todas
              </Button>
            ) : null}
          </div>

          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted">
              {query.isPending ? 'Cargando los avisos…' : 'No hay avisos.'}
            </p>
          ) : (
            <ul className="max-h-96 divide-y divide-line overflow-y-auto">
              {items.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      open(notification);
                    }}
                    className={`block w-full px-3 py-3 text-left hover:bg-primary-soft ${
                      notification.is_read ? '' : 'bg-primary-soft/40'
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase text-muted">
                        {TYPE_LABELS[notification.type]}
                      </span>
                      <span className="text-xs text-muted">
                        {formatWhen(notification.created_at)}
                      </span>
                    </span>
                    <span className="mt-1 block text-sm font-medium text-content">
                      {notification.title}
                    </span>
                    {notification.body === null ? null : (
                      <span className="mt-0.5 block text-xs text-muted">{notification.body}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
